import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { StatChip } from '@/components/atoms';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { ClassDefinition } from '@/schemas/classDefinition';
import type { ClassId } from '@/schemas/classId';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { cn } from '@/lib/utils';

// organism — DWR class preview modal: image + intro (expandable) + stat-chips
// + footer (Annulla + CTA). Pattern .class-modal mockup (styles.css 396-554).
// Built su shadcn <Dialog> (Radix): focus trap, ESC, ARIA gratis.
// Skin DWR sovrascrive il default shadcn:
//   - paper-bright bg + squared corners
//   - doppia cornice esterna semi-trasparente (outline-double-ink/30 + offset 6px)
//   - grid 2-col (art + body+footer)
// Lo `class-art` è caricato da /classes/<classId>.png (asset originali in
// public/classes/). Mobile bottom-sheet con drag-to-dismiss del mockup → FI dedicato.
//
// **2026-05-25** — UX refinements:
//  - Rimosso il bottone X di chiusura in alto a destra (`showCloseButton={false}`).
//    Chiusura via tap backdrop, ESC, o bottone Annulla nel footer.
//  - **Carosello orizzontale a 3 slot** (`prev | current | next`): il modal
//    contiene una row `flex w-[300%]` con 3 `<ClassModalContent>` affiancati.
//    A riposo, `translateX(-33.333%)` mostra la corrente al centro; durante
//    swipe la row trascina come un treno (drag visivo 1:1 col dito); al
//    rilascio sopra soglia → snap a -66.66% (next) o 0% (prev) animato; a
//    fine animazione il caller aggiorna `classDef` e il row torna istantaneo
//    a -33.333% con il nuovo trio. UX coerente con photo viewer iOS.
//  - Convenzione iOS/carousel: gesto a destra → prev (treno scorre a destra,
//    la prev entra da sinistra); gesto a sinistra → next. Usa
//    `useSwipeNavigation` hook (FI-77 pattern: distance 60px OR velocity
//    0.4 px/ms, anti-jitter 15px, ratio orizzontale 1.5).
//  - Snap-back smooth se rilascio sotto soglia (drag offset > 0 ma non
//    sufficiente per cycle): row torna a -33.333% con transition.
//  - 2 chevron visibili (`<ChevronLeft>`/`<ChevronRight>`) ai margini
//    laterali, ai vertici della card (z-50, bg-paper opaco + border + shadow).
//    Click triggera lo stesso flow del swipe (snap + cycle).
//  - Font del testo introduttivo della classe leggermente più grande
//    (`text-[1.05rem]`) per migliore leggibilità su mobile/tablet.
//  - Pulsante CTA "Scegli {classe}" width fissa (`w-[142px]` = larghezza
//    naturale di "Scegli Guerriero"): box identico per tutte le 12 classi,
//    niente respiro durante swipe.
//  - Stat chips restano in posizione originale (dopo la description, prima
//    del footer). Provata variante "compact bar in cima su mobile" e poi
//    abbandonata: la versione compatta non risultava intuitiva (label
//    abbreviate "DAN/CAR/LEG" poco comprensibili a colpo d'occhio).

interface ClassModalProps {
  classDef: ClassDefinition | null;
  /** Classe precedente per il carosello (slot sinistro). Wrap-around dal caller. */
  prevClassDef?: ClassDefinition | undefined;
  /** Classe successiva per il carosello (slot destro). Wrap-around dal caller. */
  nextClassDef?: ClassDefinition | undefined;
  isCurrent: boolean;
  onClose: () => void;
  onChoose: () => void;
  /**
   * Naviga alla classe precedente in `dwrClasses` (alfabetico) o ciclo a
   * fine lista. Opzionale: se omesso, lo swipe destra non triggera azione.
   */
  onPrev?: (() => void) | undefined;
  /**
   * Naviga alla classe successiva in `dwrClasses` o ciclo all'inizio.
   * Opzionale: se omesso, lo swipe sinistra non triggera azione.
   */
  onNext?: (() => void) | undefined;
}

// Durata dello snap animato (transition transform 220ms = sweet spot
// "rapido ma intenzionale", coerente con Material 3 200-250ms range).
const SNAP_DURATION_MS = 220;

// Carosello state machine:
//  - 'idle': row a translateX(-33.333%), nessuna transition.
//  - 'dragging': row a translateX(-33.333% + dragOffsetPx), nessuna transition
//    (segue il dito 1:1 per fluidità).
//  - 'snapping': row anima verso target ('prev', 'next', 'center') con
//    transition. A fine animazione (timeout SNAP_DURATION_MS) il caller
//    aggiorna `classDef` e il `useEffect` resetta lo stato a 'idle'.
type CarouselPhase = 'idle' | 'dragging' | 'snapping';
type SnapTarget = 'prev' | 'next' | 'center';

export function ClassModal({
  classDef,
  prevClassDef,
  nextClassDef,
  isCurrent,
  onClose,
  onChoose,
  onPrev,
  onNext,
}: ClassModalProps) {
  const carousel = useCarouselState({
    classKey: classDef?.id ?? null,
    onPrev,
    onNext,
  });
  const canCycle = Boolean(onPrev && onNext);
  const rowStyle = computeRowStyle(carousel.state);

  return (
    <Dialog
      open={!!classDef}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        style={{
          boxShadow:
            '0 0 0 1px var(--color-ink), 0 0 0 7px var(--color-paper), 0 0 0 8px rgba(24,19,13,0.4)',
        }}
        // touch-pan-y: preserva lo scroll verticale nativo; lo swipe orizzontale
        // viene riconosciuto via ratio |dx|/|dy| in onTouchEnd dell'hook.
        // **2026-05-25 (round 8)** — `overflow-visible` su lg+ + inner wrapper
        // overflow-hidden: necessario per chevron esterni `absolute` con offset
        // negativo (`lg:left-[-60px]`/`lg:right-[-60px]`) renderizzati dentro
        // DialogContent (no portal). Portal su body è stato rimosso perché
        // generava `onInteractOutside` Radix → chiusura modal ad ogni click
        // chevron, non aggirabile via preventDefault. Sotto lg, modal mantiene
        // `overflow-hidden` standard (chevron interni absolute restano nei
        // bordi del modal).
        // `grid-cols-1` (=minmax(0,1fr)): ancora la grid cell di DialogContent
        // shadcn (display: grid 1-col implicit) per evitare loop di dimensionamento
        // sul `max-content` del row carosello (chip overflow). Vedi commit
        // precedente fix chip overflow desktop.
        className="bg-paper-bright border-ink text-ink grid-cols-1 max-h-[calc(100vh-2rem)] w-[min(900px,calc(100%-3rem))] touch-pan-y overflow-hidden rounded-none border p-0 sm:max-w-none lg:overflow-visible"
        {...carousel.swipeHandlers}
        onTouchEnd={carousel.handleTouchEnd}
      >
        {canCycle && (
          <NavChevron
            direction="prev"
            onClick={() => carousel.cycle('prev')}
            disabled={carousel.state.phase === 'snapping'}
          />
        )}
        {canCycle && (
          <NavChevron
            direction="next"
            onClick={() => carousel.cycle('next')}
            disabled={carousel.state.phase === 'snapping'}
          />
        )}
        {/* Inner wrapper sempre overflow-hidden: clippa la row carosello
            indipendentemente da overflow-visible del DialogContent (necessario
            per chevron esterni). Senza, slot prev/next sarebbero visibili
            laterali sul desktop. */}
        <div className="overflow-hidden">
          <div style={rowStyle} className="flex w-[300%] max-h-[calc(100vh-2rem)]">
            <CarouselSlot classDef={prevClassDef} />
            <CarouselSlot
              classDef={classDef ?? undefined}
              isCurrent={isCurrent}
              onClose={onClose}
              onChoose={onChoose}
            />
            <CarouselSlot classDef={nextClassDef} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CarouselState {
  phase: CarouselPhase;
  snapTarget: SnapTarget | null;
  dragOffsetPx: number;
}

interface UseCarouselStateOptions {
  classKey: string | null;
  onPrev: (() => void) | undefined;
  onNext: (() => void) | undefined;
}

/**
 * State machine + handlers per il carosello a 3 slot. Estratta da
 * `<ClassModal>` per rispettare il limite `max-lines-per-function: 100`
 * dell'ESLint config.
 */
function useCarouselState({ classKey, onPrev, onNext }: UseCarouselStateOptions) {
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [phase, setPhase] = useState<CarouselPhase>('idle');
  const [snapTarget, setSnapTarget] = useState<SnapTarget | null>(null);
  // Tracking sincrono "il cycle è stato triggerato durante il touchend?":
  // serve perché `handleTouchEnd` deve decidere se fare snap-back o lasciare
  // che il cycle gestisca lo snap. useState sarebbe stale dentro lo stesso tick.
  const cycleTriggeredRef = useRef(false);

  // Reset stato quando il caller cambia la classe in preview (post snap a
  // 'next'/'prev' il currentId si aggiorna in parent → siamo qui).
  // Reset SENZA transizione: la row torna istantaneamente a -33.333% e mostra
  // il nuovo trio (prev/current/next aggiornati) → snap visivo invisibile.
  useEffect(() => {
    setDragOffsetPx(0);
    setPhase('idle');
    setSnapTarget(null);
  }, [classKey]);

  const cycle = useCallback(
    (direction: 'prev' | 'next') => {
      if (phase === 'snapping') return;
      const handler = direction === 'next' ? onNext : onPrev;
      if (!handler) return;
      cycleTriggeredRef.current = true;
      setPhase('snapping');
      setSnapTarget(direction);
      setDragOffsetPx(0);
      window.setTimeout(handler, SNAP_DURATION_MS);
    },
    [phase, onNext, onPrev],
  );

  const swipeHandlers = useSwipeNavigation({
    onSwipeRight: () => cycle('prev'),
    onSwipeLeft: () => cycle('next'),
    onSwipeMove: (dx) => {
      if (phase === 'snapping') return;
      setPhase('dragging');
      setDragOffsetPx(dx);
    },
  });

  const handleTouchEnd = useCallback(
    (e: Parameters<typeof swipeHandlers.onTouchEnd>[0]): void => {
      cycleTriggeredRef.current = false;
      swipeHandlers.onTouchEnd(e);
      if (cycleTriggeredRef.current) return; // cycle ha già preso il controllo
      // Snap-back al centro: row anima a -33.333% (no cycle), poi torna idle.
      setDragOffsetPx((current) => {
        if (current === 0) return current;
        setPhase('snapping');
        setSnapTarget('center');
        window.setTimeout(() => {
          setPhase('idle');
          setSnapTarget(null);
        }, SNAP_DURATION_MS);
        return 0;
      });
    },
    [swipeHandlers],
  );

  const state: CarouselState = { phase, snapTarget, dragOffsetPx };
  return { state, cycle, swipeHandlers, handleTouchEnd };
}

interface CarouselSlotProps {
  classDef: ClassDefinition | undefined;
  isCurrent?: boolean;
  onClose?: () => void;
  onChoose?: () => void;
}

/**
 * Slot di larghezza fissa (1/3 della row = 100% modal width) che contiene
 * una singola `<ClassModalContent>`. Le grid-area (md+) per layout 2-col
 * art|body vivono qui, non sulla row carosello (la row è solo un flex
 * orizzontale per il drag).
 */
function CarouselSlot({ classDef, isCurrent = false, onClose, onChoose }: CarouselSlotProps) {
  // **2026-05-25** — Width slot ancorato a 1/3 della row carosello:
  //   - `basis-1/3 shrink-0 grow-0`: flex layout forza basis 33.333% senza
  //     consentire shrink/grow. Necessario perché `w-1/3` da solo permette
  //     comunque al content interno (chip lunghi delle classi caster) di
  //     spingere oltre, espandendo la row carosello e causando overflow.
  //   - `md:grid-cols-[320px_minmax(0,1fr)]`: la cella body (2°) deve poter
  //     shrinkare entro la larghezza del modal. Senza `minmax(0, 1fr)` la
  //     cella default è `auto` (= `min-content`) e i chip lunghi delle classi
  //     caster ("Magia della Natura", "Magia Divini · del Patto") espandono
  //     la cella → conferma overflow orizzontale anche con flex-shrink-0.
  return (
    <div className="grid min-w-0 shrink-0 grow-0 basis-1/3 grid-cols-1 grid-rows-[1fr_auto] gap-0 overflow-y-auto md:grid-cols-[320px_minmax(0,1fr)] md:grid-rows-[1fr_auto] md:[grid-template-areas:'art_body'_'art_footer']">
      {classDef ? (
        <ClassModalContent
          classDef={classDef}
          isCurrent={isCurrent}
          onClose={onClose ?? noop}
          onChoose={onChoose ?? noop}
        />
      ) : null}
    </div>
  );
}

const noop = (): void => {
  /* placeholder per slot prev/next: l'utente non interagisce con bottoni
     interni di card fuori-vista; quando una di queste diventa current, il
     parent SectionClass passa onClose/onChoose veri. */
};

/**
 * Calcola lo stile della row carosello (transform translateX in % + px).
 *
 * Row width 300% = 3 slot affiancati. Posizioni dei 3 slot relative alla row:
 *  - slot prev (1°): offset 0% — visibile quando row a translateX(0%)
 *  - slot current (2°): offset 33.333% — visibile quando row a translateX(-33.333%)
 *  - slot next (3°): offset 66.666% — visibile quando row a translateX(-66.666%)
 *
 * Phase mapping:
 *  - 'idle': row a -33.333%, niente transition (snap istantaneo dopo cycle).
 *  - 'dragging': row a `calc(-33.333% + dragOffsetPx)`, niente transition (drag fluido).
 *  - 'snapping' target 'prev': row anima a 0% con transition (mostra prev al centro).
 *  - 'snapping' target 'next': row anima a -66.666% (mostra next al centro).
 *  - 'snapping' target 'center': row anima a -33.333% (snap-back).
 */
function computeRowStyle(args: CarouselState): CSSProperties {
  const { phase, snapTarget, dragOffsetPx } = args;
  if (phase === 'snapping' && snapTarget !== null) {
    const targetPct = SNAP_TARGET_TRANSLATE_PCT[snapTarget];
    return {
      transform: `translateX(${targetPct})`,
      transition: `transform ${SNAP_DURATION_MS}ms cubic-bezier(0.2, 0.9, 0.3, 1)`,
    };
  }
  if (phase === 'dragging' && dragOffsetPx !== 0) {
    return {
      transform: `translateX(calc(-33.333% + ${dragOffsetPx}px))`,
      transition: 'none',
    };
  }
  return {
    transform: 'translateX(-33.333%)',
    transition: 'none',
  };
}

const SNAP_TARGET_TRANSLATE_PCT: Record<SnapTarget, string> = {
  prev: '0%',
  next: '-66.666%',
  center: '-33.333%',
};

interface NavChevronProps {
  direction: 'prev' | 'next';
  onClick: () => void;
  disabled: boolean;
}

function NavChevron({ direction, onClick, disabled }: NavChevronProps) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  const ariaLabel = direction === 'prev' ? 'Classe precedente' : 'Classe successiva';
  // **2026-05-25 (round 8)** — Posizionamento responsive in unico button
  // `position: absolute` figlio diretto di `<DialogContent>`:
  //  - **Mobile/tablet (sotto lg=1024px)**: interni al modal, ai bordi
  //    laterali (`left-1`/`right-1`), `top-[58%]` per coprire meno
  //    l'immagine art (aspect 3/4 su mobile).
  //  - **Desktop (lg+)**: offset negativo `lg:left-[-60px]` /
  //    `lg:right-[-60px]` per renderli ESTERNI al modal. Possibile grazie a
  //    `lg:overflow-visible` su DialogContent + inner wrapper della row
  //    carosello con `overflow-hidden` (clippa il treno carosello senza
  //    nascondere i chevron). 60px = button width 44 + gap 16 dal modal.
  //    Centrato verticalmente `lg:top-1/2`.
  //
  // **Perché niente portal**: tentativo precedente con `createPortal` su
  // `document.body` abbandonato. Radix tratta TUTTO ciò che è fuori del
  // DialogContent come "click outside" → `onPointerDownOutside` chiude il
  // modal. `e.preventDefault()` non basta perché Radix combina più
  // listener (pointerdown + focus + interact) e il close avviene comunque.
  // Mantenendo il chevron DENTRO DialogContent come `absolute`, Radix lo
  // considera "interno" e non chiude.
  const position =
    direction === 'prev'
      ? 'left-1 lg:left-[-60px] top-[58%] lg:top-1/2'
      : 'right-1 lg:right-[-60px] top-[58%] lg:top-1/2';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'border-ink bg-paper-bright text-ink hover:bg-ink hover:text-paper-bright absolute z-50 grid h-11 w-11 -translate-y-1/2 cursor-pointer place-items-center rounded-full border-[1.5px] shadow-md transition-colors duration-150 disabled:cursor-default disabled:opacity-30',
        position,
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={2.4} aria-hidden />
    </button>
  );
}

interface ClassModalContentProps {
  classDef: ClassDefinition;
  isCurrent: boolean;
  onClose: () => void;
  onChoose: () => void;
}

function ClassModalContent({ classDef, isCurrent, onClose, onChoose }: ClassModalContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // Reset expand state quando cambia la classe in preview
  useEffect(() => {
    setIsExpanded(false);
  }, [classDef.id]);

  const cleanedIntro = stripMarkdown(classDef.intro);
  const snippet = makeSnippet(cleanedIntro);
  const hasMore = cleanedIntro.length > snippet.length;
  const spellLabel = SPELLCASTING_LABELS[classDef.id];

  return (
    <>
      <div className="bg-paper-soft border-rule flex items-center justify-center border-b p-[1.5rem_1.25rem_1.25rem] md:border-r md:border-b-0 md:[grid-area:art]">
        <ClassArt classId={classDef.id} name={classDef.name} />
      </div>

      <div className="min-w-0 overflow-y-auto p-[1.5rem_1rem_0.5rem] md:max-h-[calc(100vh-8rem)] md:p-[1.5rem_1.75rem_0.5rem] md:[grid-area:body]">
        <div className="font-display text-ink-faint mb-1 text-[0.7rem] tracking-[0.18em] uppercase">
          Classe
        </div>
        <DialogTitle className="font-display mt-0 mb-3 text-[clamp(1.7rem,3vw,2.4rem)] leading-[1.05] tracking-[0.05em] uppercase">
          Il {classDef.name}
        </DialogTitle>
        <DialogDescription asChild>
          {/* **2026-05-25** — font del corpo descrittivo aumentato a 1.05rem
              (≈17px) per migliore leggibilità su mobile/tablet. Titolo
              `Il {classDef.name}` e bottoni Annulla/Scegli invariati.
              **`md:min-h-[12rem]`** (192px) su md+: stabilizza l'altezza
              del modal durante lo swipe. Lo snippet (~400 char) ha line-wrap
              variabile da classe a classe (range misurato 82-191px); il
              min-h porta tutte al massimo → altezza modal uniforme. Solo
              md+ per non sprecare spazio su mobile dove la finestra è
              comunque schermo-pieno. */}
          <p className="text-ink-soft m-0 text-[1.05rem] leading-relaxed whitespace-pre-line italic md:min-h-[12rem]">
            {isExpanded ? cleanedIntro : snippet}
          </p>
        </DialogDescription>
        {hasMore ? (
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="font-display text-ink-soft hover:text-ink mt-2 inline-flex items-center gap-1 bg-transparent text-[0.7rem] tracking-[0.1em] uppercase transition-colors"
            aria-expanded={isExpanded}
          >
            <span>{isExpanded ? 'Mostra meno' : 'Mostra tutto'}</span>
            {isExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
        ) : null}
        {/* **2026-05-25** — `md:min-h-[4.5rem]` (72px) stabilizza l'altezza
            anche quando i chip stanno su 1 sola riga (es. classi non
            caster, ~32px naturali). 72px = altezza con 2 righe (caster con
            label "Magia ..." che va a capo). Tutte le classi su md+ hanno
            chip container di altezza uniforme → modal altezza identica.
            `items-start content-start`: necessari per evitare che flex
            default (`align-items: stretch`) allunghi i chip verticalmente
            quando il container ha `min-h` maggiore del content naturale.
            Allineamento ancorato in alto, spazio extra resta vuoto sotto. */}
        <div className="mt-4 mb-4 flex flex-wrap content-start items-start gap-2 md:min-h-[4.5rem]">
          <StatChip label="Danno" value={classDef.damageDie} />
          <StatChip label="PF base" value={classDef.baseHp} />
          <StatChip label="Carico" value={classDef.baseLoad} />
          <StatChip label="Legami" value={classDef.maxBonds} />
          {spellLabel ? <StatChip label="Magia" value={spellLabel} /> : null}
        </div>
      </div>

      <div className="bg-paper-soft border-rule flex justify-end gap-[0.6rem] border-t p-[1rem_1.75rem_1.25rem] md:[grid-area:footer]">
        <button
          type="button"
          onClick={onClose}
          className="font-display border-ink-soft text-ink hover:bg-paper-bright cursor-pointer border-[1.5px] bg-transparent px-[1.4em] py-[0.6em] text-[0.85rem] font-bold tracking-[0.08em] uppercase transition-[background-color] duration-150"
        >
          Annulla
        </button>
        {/* **2026-05-25** — Width fissa basata sulla larghezza naturale di
            "Scegli Guerriero" (classe con nome più lungo del catalogo) per
            evitare che il pulsante respiri tra classe e classe durante
            l'animazione swipe. Il box mantiene la stessa larghezza per tutte
            le 12 classi: i nomi brevi ("Scegli Mago") stanno su 1 riga
            centrata, quelli lunghi ("Scegli Guerriero") vanno su 2 righe come
            era nella versione pre-swipe. `text-center` per centrare entrambe
            le forme nel box di larghezza fissa. */}
        <button
          type="button"
          onClick={onChoose}
          disabled={isCurrent}
          className={cn(
            'font-display border-ink w-[142px] border-[1.5px] px-[1.4em] py-[0.6em] text-center text-[0.85rem] font-bold tracking-[0.08em] uppercase transition-[background-color,border-color] duration-150',
            isCurrent
              ? 'bg-paper-bright text-ink cursor-default'
              : 'bg-ink text-paper-bright hover:bg-accent-dwr hover:border-accent-dwr cursor-pointer',
          )}
        >
          {isCurrent ? '✓ Già scelta' : `Scegli ${classDef.name}`}
        </button>
      </div>
    </>
  );
}

function ClassArt({ classId, name }: { classId: string; name: string }) {
  return (
    <figure className="border-ink m-0 aspect-[3/4] w-full overflow-hidden border bg-white">
      <img
        src={`/classes/${classId}.png`}
        alt={`Illustrazione del ${name}`}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-contain"
      />
    </figure>
  );
}

// Lookup label "tipo di magia" per classe casteggiante. Usato nel chip "Magia"
// del modal. Sostituisce la label tecnica cantripLabel (Trucchetti/Orazioni/
// Suppliche) con una descrizione del *tipo* di incantesimi della classe.
const SPELLCASTING_LABELS: Partial<Record<ClassId, string>> = {
  mago: 'Arcani',
  chierico: 'Divini',
  druido: 'della Natura',
  paladino: 'Divini · del Patto',
  warlock: 'del Patto',
};

function stripMarkdown(intro: string): string {
  return intro.replace(/[*_]/g, '');
}

/**
 * Snippet ~400 caratteri / 2-3 frasi per la preview iniziale. L'intro pieno è
 * disponibile via toggle "Mostra tutto" del modal.
 */
function makeSnippet(cleaned: string): string {
  if (cleaned.length <= 400) return cleaned;
  const slice = cleaned.slice(0, 400);
  const lastDot = slice.lastIndexOf('.');
  return (lastDot > 200 ? slice.slice(0, lastDot + 1) : slice).trimEnd() + '…';
}
